import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import "../styles/OrderHistory.css";

export default function OrderHistory() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loadingItems, setLoadingItems] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /* ================= FETCH ORDERS ================= */
  useEffect(() => {
    const fetchOrders = async () => {
      setPageLoading(true);

      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.user_id) {
          setOrders([]);
          setFilteredOrders([]);
          return;
        }

        const response = await fetch(`${API_BASE_URL}order_list.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.user_id }),
        });

        const data = await response.json();
        console.log("line 36 :", data);
        if (data.head.code !== 200) {
          setOrders([]);
          setFilteredOrders([]);
          return;
        }

        const transformedOrders = data.body.map((order) => {
          const items = order.items.map((item) => ({
            id: item.productcode,
            mpid: item.mpid,
            pid: item.pid,
            title: item.productname,
            image: item.producturl,
            type: item.type, // "Hardcopy" | "Subscription"
            plan_type: item.plan_type, 
            quantity: Number(item.quantity),
            price: Number(item.subtotal),
            total: Number(item.total),
            status: item.orderstatus || "-",
          }));

          return {
            id: order.order_id,
            date: order.order_date,
            items,
            total: Number(order.items[0]?.order_total || 0),
            deliveryCharge: Number(order.delivery_charge || 500),
            status: items.every((i) => i.status === "Cancelled")
              ? "Cancelled"
              : "Accepted",
          };
        });

        setOrders(transformedOrders);
        setFilteredOrders(transformedOrders);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        setPageLoading(false);
      }
    };

    fetchOrders();
  }, [API_BASE_URL]);

  /* ================= DATE FILTER ================= */
  useEffect(() => {
    let filtered = [...orders];

    if (fromDate) {
      filtered = filtered.filter((o) => o.date.split(" ")[0] >= fromDate);
    }

    if (toDate) {
      filtered = filtered.filter((o) => o.date.split(" ")[0] <= toDate);
    }

    setFilteredOrders(filtered);
  }, [fromDate, toDate, orders]);

  /* ================= CANCEL ITEM ================= */
  const handleCancelItem = async (orderId, itemId) => {
    if (!window.confirm("Cancel this item?")) return;

    const currentOrder = orders.find((o) => o.id === orderId);
    if (!currentOrder) return;

    const itemToCancel = currentOrder.items.find((i) => i.id === itemId);

    // Get list of active items (excluding the one being cancelled)
    const activeItems = currentOrder.items.filter(
      (i) => i.id !== itemId && i.status !== "Cancelled"
    );

    // --- 1. Calculate New Delivery Charge ---
    let newDeliveryCharge = currentOrder.deliveryCharge;

    if (itemToCancel.type === "Hardcopy") {
      const remainingHardcopies = activeItems.filter(
        (i) => i.type === "Hardcopy"
      );
      // If no hardcopies remain, delivery charge becomes 0
      if (remainingHardcopies.length === 0) {
        newDeliveryCharge = 0;
      }
    }

    // --- 2. Calculate New Order Total ---
    const activeItemsTotal = activeItems.reduce((sum, i) => sum + i.total, 0);
    // Total is items + delivery (if items exist), otherwise 0
    const newOrderTotal =
      activeItems.length === 0 ? 0 : activeItemsTotal + newDeliveryCharge;

    setLoadingItems((prev) => [...prev, itemId]);
    console.log("mpid: ", itemToCancel.mpid);
    console.log("Pid: ", itemToCancel.pid);
    console.log("quantity: ", itemToCancel.quantity);
    console.log("type:", itemToCancel.type);
    console.log("plan_type:", itemToCancel.plan_type);
    try {
      const response = await fetch(`${API_BASE_URL}cancel_order.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: orderId,
          product_code: itemId,
          mp_id: itemToCancel.mpid,
          quantity: itemToCancel.quantity,
          p_id: itemToCancel.pid,
          type: itemToCancel.type,
          updated_total: newOrderTotal, // <--- Sending updated total
          updated_delivery_charge: newDeliveryCharge, // <--- Sending updated delivery charge
          plan_type: itemToCancel.plan_type,
        }),
      });

      const data = await response.json();

      if (data.head.code !== 200) {
        alert(data.head.msg || "Cancellation failed");
        return;
      }

      // --- 3. Update State ---
      const updatedOrders = orders.map((order) => {
        if (order.id !== orderId) return order;

        const updatedItems = order.items.map((item) =>
          item.id === itemId ? { ...item, status: "Cancelled" } : item
        );

        const allCancelled = updatedItems.every(
          (i) => i.status === "Cancelled"
        );

        return {
          ...order,
          items: updatedItems,
          total: newOrderTotal,
          deliveryCharge: newDeliveryCharge,
          status: allCancelled ? "Cancelled" : "Accepted",
        };
      });

      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);
    } catch (err) {
      console.error("Cancel failed:", err);
      alert("Something went wrong while cancelling.");
    } finally {
      setLoadingItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

const isSubscriptionCancelExpired = (orderDate) => {
  if (!orderDate) return true;

  // Convert "YYYY-MM-DD HH:mm:ss" → Date
  const orderTime = new Date(orderDate.replace(" ", "T"));
  const now = new Date();

  return now - orderTime >= FOUR_HOURS_MS;
};


  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d)) return "";
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  // Page loader
  const PageLoader = () => {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#ffffff",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ width: "3.5rem", height: "3.5rem", color: "#f64a1aff" }}
        />
        <p className="mt-3 fw-semibold">Loading Your Orders...</p>
      </div>
    );
  };

  return (
    <>
      {pageLoading && <PageLoader />}

      <div className="container-fluid orderhistory-container">
        <h2 className="mb-4">Order History</h2>

        {/* FILTER */}
        <div className="border rounded p-3 mb-4 bg-white">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small fw-semibold">From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label small fw-semibold">To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-6 text-end">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* TABLE */}
        {filteredOrders.length === 0 ? (
          <p className="text-muted">No orders found.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered border-dark-subtle align-middle text-center">
              <thead className="table-light border-dark-subtle">
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Image</th>
                  <th>Product Name</th>
                  <th>Code</th>
                  <th>Qty</th>
                  <th>Sub Total</th>
                  <th>Total(incl Taxes)</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredOrders.map((order) =>
                  order.items.map((item, index) => (
                    <tr key={`${order.id}-${item.id}`}>
                      {index === 0 && (
                        <>
                          <td
                            className="align-top"
                            rowSpan={order.items.length}
                          >
                            {order.id}
                          </td>
                          <td
                            className="align-top"
                            rowSpan={order.items.length}
                          >
                            {order.date}
                          </td>
                        </>
                      )}

                      <td>
                        <img src={item.image} width="80" alt="" />
                      </td>
                      <td className="text-start">{item.title}</td>
                      <td>{item.id}</td>
                      <td>{item.quantity}</td>
                      <td className="text-end">₹ {item.price.toFixed(2)}</td>

                      {index === 0 && (
                        <td className="text-end" rowSpan={order.items.length}>
                          ₹ {Math.round(order.total).toFixed(2)}
                        </td>
                      )}

                      <td
                        className={
                          item.status === "Cancelled"
                            ? "text-danger fw-semibold"
                            : "text-primary fw-semibold"
                        }
                      >
                        {item.status}
                      </td>

                      <td>
                        {item.status === "Accepted" ? (
  <button
    className="btn btn-danger btn-sm"
    onClick={() => handleCancelItem(order.id, item.id)}
    disabled={
      loadingItems.includes(item.id) ||
      (item.type === "Subscription" &&
        isSubscriptionCancelExpired(order.date))
    }
  >

                            <Trash2 size={20} style={{ paddingRight: "5px" }} />
                            {loadingItems.includes(item.id)
  ? "Cancelling..."
  : item.type === "Subscription" &&
    isSubscriptionCancelExpired(order.date)
  ? "Cancellation Closed"
  : "Cancel"}

                          </button>
                        ) : (
                          <span className="fw-semibold">{item.status}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
